import React, { useContext, useState, useEffect, useRef, useMemo } from 'react'
import AuthContext from '../../context/AuthContext'
import { Breadcrumb, BreadcrumbItem, Tile } from 'carbon-components-react'
import Toast from '../../components/Notification/Toast'
import { CountriesList } from './countries.tsx';

const { REACT_APP_DEV_API_URL, REACT_APP_PROD_API_URL, REACT_APP_DEV_MEDIA_URL, REACT_APP_PROD_MEDIA_URL, NODE_ENV } = process.env;
const API_URL =
    NODE_ENV === 'production' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL;
const MEDIA_URL =
    NODE_ENV === 'production' ? process.env.REACT_APP_PROD_MEDIA_URL : process.env.REACT_APP_DEV_MEDIA_URL;

const CountriesSettings = ({ match }) => {
    // Variables for pagination
    const [totalItems, setTotalItems] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [firstRowIndex, setFirstRowIndex] = useState(0);

    // Variables for modals and toasts
    const [list, setList] = useState([]);
    let toastProperties = null;
    let [openAddModal, setOpenAddModal] = useState(false)
    let [openEditModal, setOpenEditModal] = useState(false)
    let [openDeleteModal, setOpenDeleteModal] = useState(false)
    let [editID, setEditID] = useState(null)
    let [deleteID, setDeleteID] = useState(null)
    let [error, setError] = useState(null)

    // Other Variables
    let { user, authTokens } = useContext(AuthContext)
    let [countries, setCountries] = useState([])

    // Variables for Search
    const [countrySearch, setCountrySearch] = useState("");

    const addForm = useRef();
    const editForm = useRef();

    useEffect(() => {
        const getCountries = async () => {
            let response = await fetch(`${API_URL}/system/referencedata/geographical/countries/`, {
                method: 'GET',
                headers:{
                    'Content-Type':'application/json',
                    'Authorization':'Bearer ' + authTokens?.access
                }
            })
            let data = await response.json()
            setCountries(data)
        }
        getCountries()
        setTotalItems(countries?.length)
        document.title = "Countries Reference Data | WillowBMS"
    }, [])

    const showToast = (type, title, description) => {
        const id = Math.floor((Math.random() * 100) + 1);
        const time = new Date().toLocaleString();

        switch (type) {
            case 'success':
                toastProperties = {
                    id,
                    title: title,
                    description: description,
                    kind: 'success',
                    time: time
                }
                break;
            case 'error':
                toastProperties = {
                    id,
                    title: title,
                    description: description,
                    kind: 'error',
                    time: time
                }
                break;
            case 'info':
                toastProperties = {
                    id,
                    title: title,
                    description: description,
                    kind: 'info',
                    time: time
                }
                break;
            case 'warning':
                toastProperties = {
                    id,
                    title: title,
                    description: description,
                    kind: 'warning',
                    time: time
                }
                break;
            default:
                setList([]);
        }
        setList([...list, toastProperties]);
    }

    let getCountryList = async () => {
        let response = await fetch(`${API_URL}/system/referencedata/geographical/countries/`, {
            method: 'GET',
            headers:{
                'Content-Type':'application/json',
                'Authorization':'Bearer ' + authTokens?.access
            }
        })
        let data = await response.json()
        setCountries(data)
    }

    let addCountry = async (values) => {
        values.preventDefault()

        let response = await fetch(`${API_URL}/system/referencedata/geographical/country/add/`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization':'Bearer ' + authTokens?.access
            },
            body: JSON.stringify({
                'country': values.target.country.value,
                'countrycode': values.target.countrycode.value,
            })
        })

        if (response.status === 200) {
            getCountryList()
            setOpenAddModal(false)
            showToast("success", "Success", "New country has been added.")
            setError(false)
            document.getElementById("addCountry").reset()
        } else {
            setError(true)
        }
    }

    let editCountry = async (values) => {
        values.preventDefault();
        let response = await fetch(`${API_URL}/system/referencedata/geographical/country/edit/${editID.id}/`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
                'Authorization':'Bearer ' + authTokens?.access
            },
            body: JSON.stringify({
                'country': values.target.country.value,
                'countrycode': values.target.countrycode.value,
            })
        })

        if (response.status === 200) {
            getCountryList()
            setOpenEditModal(false)
            showToast("success", "Success", "Country has been edited.")
            setError(false)
        } else {
            setError(true)
        }
    }

    let editSetup = async (id) => {
        countries?.map((country) => { country.id === id && setEditID(country) }
        )
    }

    let batchDelete = async (selectedCountries) => {
        selectedCountries.map((country) => deleteCountry(country.id))
    }

    let deleteCountry = async (countryID) => {
        let response = await fetch(`${API_URL}/system/referencedata/geographical/country/delete/${countryID}/`, {
            method: 'DELETE',
            'headers': {
                'Content-Type': 'application/json',
                'Authorization':'Bearer ' + authTokens?.access
            }
        })

        if (response.status === 200) {
            getCountryList()
            setOpenDeleteModal(false)
            let deleteSuccessMessage = "Selected countries (#" + countryID + ") have been delete."
            showToast("success", "Success", deleteSuccessMessage)
            setError(false)
        } else {
            setError(true)
        }
    }

    const countriesHeaders = [
        { label: "Country Description", key: "country" },
        { label: "Country Code", key: "countrycode" }
    ];

    const countryData = useMemo(() => {
        let computedCountries = search(countries);
        setTotalItems(computedCountries.length);
        //Current Page slice
        return computedCountries.slice(
            firstRowIndex,
            firstRowIndex + currentPageSize
        );
    }, [countries, firstRowIndex, currentPageSize, countrySearch]);

    const csvCountryReport = {
        data: countries,
        headers: countriesHeaders,
        filename: `WillowBMS_system_countries.csv`
    };

    function search(rows) {
        return rows.filter(
            (row) =>
                row.country.toLowerCase().indexOf(countrySearch) > -1 ||
                row.country.indexOf(countrySearch) > -1 ||
                row.countrycode.indexOf(countrySearch) > -1 ||
                row.countrycode.toLowerCase().indexOf(countrySearch) > -1
        );
    }

    return (
        <div id="body" className="bms-page d-flex">
            <Toast toastList={list} />
            <div className="wrapper">
                <Breadcrumb>
                    <BreadcrumbItem href="/system/settings/general">Settings</BreadcrumbItem>
                    <BreadcrumbItem href="/system/settings/reference-data">Reference Data</BreadcrumbItem>
                    <span>Countries</span>
                </Breadcrumb>
                <h2>
                    Reference Data
                </h2>
                <Tile light={true} className="row mt-1">
                    <h5 className="tile-title mb-1">Countries</h5>
                    <CountriesList setDeleteID={setDeleteID} csvCountryReport={csvCountryReport} setCountrySearch={setCountrySearch} deleteID={deleteID} batchDelete={batchDelete} openDeleteModal={openDeleteModal} setOpenDeleteModal={setOpenDeleteModal} editID={editID} editSetup={editSetup} editForm={editForm} openEditModal={openEditModal} setOpenEditModal={setOpenEditModal} editCountry={editCountry} addCountry={addCountry} addForm={addForm} openAddModal={openAddModal} setOpenAddModal={setOpenAddModal} error={error} setError={setError} rows={countryData} totalItems={totalItems} currentPageSize={currentPageSize} setCurrentPageSize={setCurrentPageSize} setFirstRowIndex={setFirstRowIndex} />
                </Tile>
            </div>
        </div>
    )
}

export default CountriesSettings